import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi, documentiUtenteApi, adminsApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { 
  Plus, Search, Edit2, Trash2, User, Mail, Phone, Shield, UserCheck, 
  FileText, Download, Upload, Eye, CreditCard, Briefcase, Home,
  Key, Copy, CheckCircle2, AlertCircle, FileSpreadsheet, ArrowUpDown, ArrowUp, ArrowDown, Lock, Unlock, ShieldCheck, ShieldOff, Send, Clock, Ban
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import QuickEmailDialog from '../../components/QuickEmailDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const tipiUtente = [
  // Tipo utente non più necessario - il ruolo è contestuale
  // Mantenuto per retrocompatibilità nella visualizzazione
  { value: 'proprietario', label: 'Proprietario' },
  { value: 'inquilino', label: 'Inquilino' },
  { value: 'garante', label: 'Garante' },
];

const tipiDocumentoPersonali = [
  { value: 'carta_identita', label: 'Carta d\'Identità' },
  { value: 'patente', label: 'Patente' },
  { value: 'passaporto', label: 'Passaporto' },
  { value: 'codice_fiscale', label: 'Codice Fiscale' },
  { value: 'coordinate_bancarie', label: 'Coordinate Bancarie (IBAN)' },
];

const tipiDocumentoReddituali = [
  { value: 'dichiarazione_redditi', label: '730/Unico o CUD' },
  { value: 'isee', label: 'ISEE (DSU + Attestazione)' },
  { value: 'buste_paga', label: 'Ultime 3 buste paga / 5 fatture' },
  { value: 'visura_camerale', label: 'Visura Camerale' },
];

const tipiDocumentoDebitorie = [
  { value: 'finanziamenti', label: 'Finanziamenti' },
  { value: 'mutui', label: 'Mutui' },
  { value: 'mantenimento', label: 'Mantenimento ex coniuge/figli' },
  { value: 'cessione_quinto', label: 'Cessione del 5°' },
];

const UtentiPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [documenti, setDocumenti] = useState([]);
  const [formData, setFormData] = useState(getEmptyFormData());
  const [passwordSetupLink, setPasswordSetupLink] = useState(null);
  const [cfValidation, setCfValidation] = useState({ valid: null, message: '' });
  const [ibanValidation, setIbanValidation] = useState({ valid: null, message: '' });
  const [comuniSuggestions, setComuniSuggestions] = useState([]);
  const [comuniNascitaSuggestions, setComuniNascitaSuggestions] = useState([]);
  const [comuniSedeSuggestions, setComuniSedeSuggestions] = useState([]);
  const [comuniRappresentanteSuggestions, setComuniRappresentanteSuggestions] = useState([]);
  const [filterPwdPending, setFilterPwdPending] = useState(false);
  const [filterInactive, setFilterInactive] = useState(false);
  const [filterDeactivated, setFilterDeactivated] = useState(false);
  const [comuniNascitaRappSuggestions, setComuniNascitaRappSuggestions] = useState([]);
  const [searchingComuni, setSearchingComuni] = useState(false);
  const [searchingComuniNascita, setSearchingComuniNascita] = useState(false);
  const [searchingSede, setSearchingSede] = useState(false);
  const [searchingRappresentante, setSearchingRappresentante] = useState(false);
  const [searchingNascitaRapp, setSearchingNascitaRapp] = useState(false);
  const [quickEmailUser, setQuickEmailUser] = useState(null);

  // Admin management
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [adminFormData, setAdminFormData] = useState({ nome: '', cognome: '', email: '', password: '', telefono: '' });
  
  // Ordinamento colonne
  const [sortColumn, setSortColumn] = useState('cognome');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [docFormData, setDocFormData] = useState({
    categoria: 'personali',
    tipo_documento: '',
    numero_documento: '',
    rilasciato_da: '',
    data_rilascio: '',
    data_scadenza: '',
    note: '',
    file: null
  });
  const [docFileInputKey, setDocFileInputKey] = useState(0);

  function getEmptyFormData() {
    return {
      email: '', password: '', nome: '', cognome: '', codice_fiscale: '',
      data_nascita: '', luogo_nascita: '', provincia_nascita: '',
      telefono: '', pec: '', altro_contatto: '',
      indirizzo_residenza: '', cap_residenza: '', citta_residenza: '', provincia_residenza: '',
      sdi: '', iban: '', intestazione_vop: '',
      // Dati Societari
      is_societa: false,
      ragione_sociale: '',
      partita_iva: '',
      sede_legale: '',
      sede_legale_indirizzo: '',
      sede_legale_citta: '',
      sede_legale_cap: '',
      sede_legale_provincia: '',
      rappresentante_legale: '',
      cf_rappresentante: '',
      rappresentante_data_nascita: '',
      rappresentante_luogo_nascita: '',
      rappresentante_provincia_nascita: '',
      rappresentante_indirizzo: '',
      rappresentante_citta: '',
      rappresentante_cap: '',
      rappresentante_provincia: '',
    };
  }

  const fetchUsers = useCallback(async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchDocumenti = async (userId) => {
    try {
      const response = await documentiUtenteApi.getAll(userId);
      setDocumenti(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento documenti');
    }
  };

  // Validazione Codice Fiscale
  const validateCodiceFiscale = async (cf) => {
    if (!cf || cf.length < 16) {
      setCfValidation({ valid: null, message: '' });
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/validate-cf`, { codice_fiscale: cf });
      setCfValidation({ valid: response.data.valid, message: response.data.message });
    } catch (error) {
      setCfValidation({ valid: false, message: 'Errore nella validazione' });
    }
  };

  // Validazione IBAN
  const validateIban = async (iban) => {
    const clean = (iban || '').replace(/\s/g, '').toUpperCase();
    if (!clean) {
      setIbanValidation({ valid: null, message: '' });
      return;
    }
    if (clean.length !== 27) {
      setIbanValidation({
        valid: false,
        message: `IBAN incompleto: un IBAN italiano ha 27 caratteri (inseriti ${clean.length}).`,
      });
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/validate-iban`, { iban: clean });
      setIbanValidation({ valid: response.data.valid, message: response.data.message });
    } catch (error) {
      setIbanValidation({ valid: false, message: 'Errore nella validazione' });
    }
  };

  // Reset password utente
  const handleResetPassword = async (user) => {
    try {
      const response = await axios.post(`${API_URL}/users/${user.id}/reset-password`);
      setPasswordSetupLink(response.data.setup_token);
      setSelectedUser(user);
      setShowPasswordDialog(true);
      toast.success('Link di reset generato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset password');
    }
  };

  // Sblocca account
  const handleUnlock = async (user) => {
    if (!window.confirm(`Sbloccare l'account di ${user.cognome} ${user.nome} (${user.email})?`)) return;
    try {
      await axios.post(`${API_URL}/users/${user.id}/unlock`);
      toast.success('Account sbloccato');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore sblocco account');
    }
  };

  // Disattivazione / Riattivazione manuale utente
  const handleToggleActive = async (user) => {
    const label = `${user.cognome || ''} ${user.nome || ''}`.trim() || user.email;
    if (user.locked) {
      if (!window.confirm(`Riattivare l'account di ${label} (${user.email})?\n\nL'utente potrà nuovamente accedere al portale.`)) return;
      try {
        await usersApi.reactivate(user.id);
        toast.success('Utente riattivato');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore riattivazione utente');
      }
    } else {
      if (!window.confirm(`Disattivare l'account di ${label} (${user.email})?\n\nL'utente NON potrà più accedere al portale finché non lo riattivi.`)) return;
      try {
        await usersApi.deactivate(user.id);
        toast.success('Utente disattivato');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore disattivazione utente');
      }
    }
  };

  // Reset 2FA
  const handleReset2FA = async (user) => {
    if (!window.confirm(
      `Reset 2FA per ${user.cognome} ${user.nome} (${user.email})?\n\n`
      + `Il 2FA verra' DISATTIVATO e i codici di recupero invalidati.\n`
      + `Al prossimo accesso l'utente potra' riconfigurarlo (se richiesto dal ruolo).`
    )) return;
    try {
      await axios.post(`${API_URL}/users/${user.id}/2fa/reset`);
      toast.success('2FA disattivato per l\'utente');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore reset 2FA');
    }
  };

  // Invio email benvenuto manuale
  const handleSendWelcomeEmail = async (user) => {
    const label = `${user.cognome || ''} ${user.nome || ''}`.trim() || user.email;
    if (!window.confirm(`Inviare email di benvenuto a ${label} (${user.email})?`)) return;
    try {
      const res = await usersApi.sendWelcomeEmail(user.id);
      const linkInfo = res.data.has_setup_link
        ? ' (include link setup password)'
        : ' (utente con password già impostata: solo benvenuto)';
      toast.success(`Email di benvenuto inviata a ${res.data.to}${linkInfo}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email benvenuto');
    }
  };

  // Copia link setup password
  const copySetupLink = () => {
    const setupUrl = `${window.location.origin}/setup-password?token=${passwordSetupLink}`;
    navigator.clipboard.writeText(setupUrl);
    toast.success('Link copiato negli appunti');
  };

  // GDPR — Genera PDF riepilogo dati utente (Art. 15)
  const [gdprDialog, setGdprDialog] = useState(null); // utente selezionato
  const [gdprLoading, setGdprLoading] = useState(false);
  const [gdprPdfDone, setGdprPdfDone] = useState(false);
  const [gdprEmailDone, setGdprEmailDone] = useState(false);
  const [gdprMarkOpen, setGdprMarkOpen] = useState(false);
  const [gdprTipo, setGdprTipo] = useState('accesso');
  const [gdprCanale, setGdprCanale] = useState('email');
  const [gdprNote, setGdprNote] = useState('');
  const [gdprEsito, setGdprEsito] = useState('evasa');

  const resetGdprDialog = () => {
    setGdprDialog(null);
    setGdprPdfDone(false);
    setGdprEmailDone(false);
    setGdprMarkOpen(false);
    setGdprTipo('accesso');
    setGdprCanale('email');
    setGdprNote('');
    setGdprEsito('evasa');
  };

  const handleGdprPdf = async (user) => {
    setGdprLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/users/${user.id}/gdpr-pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = `${user.cognome || ''}_${user.nome || ''}`.trim().replace(/\s+/g, '_') || user.id;
      a.href = url;
      a.download = `Riepilogo_Dati_GDPR_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setGdprPdfDone(true);
      toast.success('PDF GDPR scaricato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore generazione PDF');
    } finally {
      setGdprLoading(false);
    }
  };

  const handleGdprEmail = (user) => {
    setGdprEmailDone(true);
    // Apre il QuickEmailDialog con un template precompilato di risposta GDPR
    setQuickEmailUser({
      ...user,
      _gdpr_prefill: {
        subject: 'Riscontro alla tua richiesta di esercizio dei diritti GDPR',
        body:
`Gentile ${user.cognome || ''} ${user.nome || ''},

in riferimento alla Sua richiesta di esercizio dei diritti previsti dal Regolamento UE 2016/679 (GDPR), Le forniamo riscontro entro i 30 giorni previsti dalla normativa.

In allegato a questa email trova il documento "Riepilogo Dati Personali" che riporta tutti i dati personali da noi conservati e trattati nell'ambito della gestione del Suo rapporto contrattuale di locazione.

NOTE IMPORTANTI SULLA CONSERVAZIONE:
• I documenti contabili e fiscali (contratti registrati, fatture, ricevute) sono soggetti ad obbligo di conservazione decennale ai sensi dell'art. 2220 del Codice Civile.
• Per i dati soggetti a tale obbligo, l'eventuale esercizio del diritto di cancellazione (Art. 17 GDPR) potrà essere soddisfatto mediante anonimizzazione, mantenendo solo le informazioni strettamente necessarie agli adempimenti di legge.

Per ogni ulteriore chiarimento o per esercitare diritti aggiuntivi (rettifica, limitazione, opposizione) può scrivere a info@semplificaffitto.it.

Cordiali saluti,
Il Titolare del trattamento
EDILSTUDIO s.r.l.`,
      },
    });
  };

  const handleGdprMarkAsHandled = async () => {
    if (!gdprDialog) return;
    const azioni = [];
    if (gdprPdfDone) azioni.push('pdf_generato');
    if (gdprEmailDone) azioni.push('email_inviata');
    try {
      await axios.post(`${API_URL}/admin/gdpr-requests-log`, {
        user_id: gdprDialog.id,
        tipo_richiesta: gdprTipo,
        canale_ricezione: gdprCanale,
        note: gdprNote,
        esito: gdprEsito,
        azioni_eseguite: azioni,
      });
      toast.success('Richiesta GDPR registrata nel registro');
      resetGdprDialog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore registrazione');
    }
  };

  // Export utenti
  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/export/users/${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `utenti.${format === 'xlsx' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} completato`);
    } catch (error) {
      toast.error('Errore durante l\'export');
    }
  };

  // Debounce helper
  const debounceTimers = useRef({});
  const debounced = useCallback((key, fn, delay = 350) => {
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(fn, delay);
  }, []);

  // Handler ottimizzato per onChange - evita ri-render pesanti
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);


  // Ricerca comuni
  const searchComuni = async (query) => {
    if (!query || query.length < 2) {
      setComuniSuggestions([]);
      return;
    }
    setSearchingComuni(true);
    try {
      const response = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      // Mappa i risultati per avere sigla_provincia
      const mapped = response.data.map(c => ({
        ...c,
        sigla_provincia: c.provincia
      }));
      setComuniSuggestions(mapped.slice(0, 10)); // Max 10 risultati
    } catch (error) {
      console.error('Errore ricerca comuni:', error);
    } finally {
      setSearchingComuni(false);
    }
  };

  // Seleziona comune dal dropdown
  const selectComune = (comune) => {
    // Se ci sono più CAP (contiene virgola), non precompilare - l'utente sceglie
    const capList = comune.cap_list || [];
    const capValue = capList.length === 1 ? capList[0] : '';
    
    setFormData({
      ...formData,
      citta_residenza: comune.nome,
      provincia_residenza: comune.sigla_provincia || comune.provincia,
      cap_residenza: capValue
    });
    setComuniSuggestions([]);
  };

  // Seleziona comune per luogo nascita
  const selectComuneNascita = (comune) => {
    setFormData({
      ...formData,
      luogo_nascita: comune.nome,
      provincia_nascita: comune.sigla_provincia,
      codice_comune_nascita: comune.codice_catastale || ''
    });
    setComuniNascitaSuggestions([]);
  };

  // Ricerca comuni per nascita
  const searchComuniNascita = async (query) => {
    if (!query || query.length < 2) {
      setComuniNascitaSuggestions([]);
      return;
    }
    setSearchingComuniNascita(true);
    try {
      const response = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      const mapped = response.data.map(c => ({
        ...c,
        sigla_provincia: c.provincia,
        codice_catastale: c.codice_catastale
      }));
      setComuniNascitaSuggestions(mapped.slice(0, 10));
    } catch (error) {
      console.error('Errore ricerca comuni nascita:', error);
    } finally {
      setSearchingComuniNascita(false);
    }
  };

  // Ricerca comuni per sede legale
  const searchComuniSede = async (query) => {
    if (!query || query.length < 2) { setComuniSedeSuggestions([]); return; }
    setSearchingSede(true);
    try {
      const response = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      setComuniSedeSuggestions(response.data.map(c => ({ ...c, sigla_provincia: c.provincia })).slice(0, 10));
    } catch (e) { console.warn('searchComuniSede:', e); } finally { setSearchingSede(false); }
  };

  const selectComuneSede = (comune) => {
    const capList = comune.cap_list || [];
    setFormData({
      ...formData,
      sede_legale_citta: comune.nome,
      sede_legale_provincia: comune.sigla_provincia || comune.provincia,
      sede_legale_cap: capList.length === 1 ? capList[0] : ''
    });
    setComuniSedeSuggestions([]);
  };

  // Ricerca comuni per residenza rappresentante
  const searchComuniRappresentante = async (query) => {
    if (!query || query.length < 2) { setComuniRappresentanteSuggestions([]); return; }
    setSearchingRappresentante(true);
    try {
      const response = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      setComuniRappresentanteSuggestions(response.data.map(c => ({ ...c, sigla_provincia: c.provincia })).slice(0, 10));
    } catch (e) { console.warn('searchComuniRappresentante:', e); } finally { setSearchingRappresentante(false); }
  };

  const selectComuneRappresentante = (comune) => {
    const capList = comune.cap_list || [];
    setFormData({
      ...formData,
      rappresentante_citta: comune.nome,
      rappresentante_provincia: comune.sigla_provincia || comune.provincia,
      rappresentante_cap: capList.length === 1 ? capList[0] : ''
    });
    setComuniRappresentanteSuggestions([]);
  };

  // Ricerca comuni per nascita rappresentante
  const searchNascitaRapp = async (query) => {
    if (!query || query.length < 2) { setComuniNascitaRappSuggestions([]); return; }
    setSearchingNascitaRapp(true);
    try {
      const response = await axios.get(`${API_URL}/comuni?q=${encodeURIComponent(query)}`);
      setComuniNascitaRappSuggestions(response.data.map(c => ({ ...c, sigla_provincia: c.provincia })).slice(0, 10));
    } catch (e) { console.warn('searchNascitaRapp:', e); } finally { setSearchingNascitaRapp(false); }
  };

  const selectNascitaRapp = (comune) => {
    setFormData({
      ...formData,
      rappresentante_luogo_nascita: comune.nome,
      rappresentante_provincia_nascita: comune.sigla_provincia || comune.provincia,
    });
    setComuniNascitaRappSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };

      if (editingItem) {
        // In MODIFICA: invia anche i campi svuotati (stringa vuota) così l'admin
        // può cancellarli e lasciarli vuoti. Rimuovi solo null/undefined.
        Object.keys(data).forEach(key => {
          if (data[key] === null || data[key] === undefined) delete data[key];
        });
        const { password, ...updateData } = data;
        await usersApi.update(editingItem.id, updateData);
        toast.success('Utente aggiornato');
      } else {
        // In CREAZIONE: rimuovi i campi vuoti
        Object.keys(data).forEach(key => {
          if (data[key] === '' || data[key] === null) delete data[key];
        });
        // Crea utente senza password (utente la imposterà autonomamente)
        delete data.password; // Password non richiesta, sarà self-service
        const response = await axios.post(`${API_URL}/users`, data);

        // Se ritorna un setup_token, mostra il link
        if (response.data.password_setup_token) {
          setPasswordSetupLink(response.data.password_setup_token);
          setSelectedUser(response.data);
          setShowPasswordDialog(true);
        }
        toast.success('Utente creato - invia il link per impostare la password');
      }

      setShowDialog(false);
      setFormData(getEmptyFormData());
      setEditingItem(null);
      setCfValidation({ valid: null, message: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      email: item.email || '',
      password: '',
      nome: item.nome || '',
      cognome: item.cognome || '',
      codice_fiscale: item.codice_fiscale || '',
      data_nascita: item.data_nascita || '',
      luogo_nascita: item.luogo_nascita || '',
      provincia_nascita: item.provincia_nascita || '',
      telefono: item.telefono || '',
      pec: item.pec || '',
      altro_contatto: item.altro_contatto || '',
      indirizzo_residenza: item.indirizzo_residenza || '',
      cap_residenza: item.cap_residenza || '',
      citta_residenza: item.citta_residenza || '',
      provincia_residenza: item.provincia_residenza || '',
      sdi: item.sdi || '',
      iban: item.iban || '',
      intestazione_vop: item.intestazione_vop || '',
      // Dati Società
      is_societa: item.is_societa || false,
      ragione_sociale: item.ragione_sociale || '',
      partita_iva: item.partita_iva || '',
      sede_legale: item.sede_legale || '',
      sede_legale_indirizzo: item.sede_legale_indirizzo || '',
      sede_legale_citta: item.sede_legale_citta || '',
      sede_legale_cap: item.sede_legale_cap || '',
      sede_legale_provincia: item.sede_legale_provincia || '',
      cf_rappresentante: item.cf_rappresentante || '',
      rappresentante_data_nascita: item.rappresentante_data_nascita || '',
      rappresentante_luogo_nascita: item.rappresentante_luogo_nascita || '',
      rappresentante_provincia_nascita: item.rappresentante_provincia_nascita || '',
      rappresentante_indirizzo: item.rappresentante_indirizzo || '',
      rappresentante_citta: item.rappresentante_citta || '',
      rappresentante_cap: item.rappresentante_cap || '',
      rappresentante_provincia: item.rappresentante_provincia || '',
    });
    setShowDialog(true);
    validateIban(item.iban || '');
  };

  // ==================== ADMIN HANDLERS ====================
  const handleNewAdmin = () => {
    setEditingAdmin(null);
    setAdminFormData({ nome: '', cognome: '', email: '', password: '', telefono: '' });
    setShowAdminDialog(true);
  };

  const handleEditAdmin = (item) => {
    setEditingAdmin(item);
    setAdminFormData({
      nome: item.nome || '',
      cognome: item.cognome || '',
      email: item.email || '',
      password: '',
      telefono: item.telefono || '',
    });
    setShowAdminDialog(true);
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...adminFormData };
      // Rimuovi campi vuoti
      Object.keys(data).forEach(key => {
        if (data[key] === '' || data[key] === null) delete data[key];
      });

      if (editingAdmin) {
        await adminsApi.update(editingAdmin.id, data);
        toast.success('Amministratore aggiornato');
      } else {
        if (!data.password) {
          toast.error('La password è obbligatoria per un nuovo amministratore');
          return;
        }
        await adminsApi.create(data);
        toast.success('Amministratore creato');
      }
      setShowAdminDialog(false);
      setAdminFormData({ nome: '', cognome: '', email: '', password: '', telefono: '' });
      setEditingAdmin(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDeleteAdmin = async (item) => {
    if (!window.confirm(`Eliminare l'amministratore ${item.nome} ${item.cognome}?`)) return;
    try {
      await adminsApi.delete(item.id);
      toast.success('Amministratore eliminato');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const handleViewDocuments = async (user) => {
    setSelectedUser(user);
    await fetchDocumenti(user.id);
    setShowDocDialog(true);
  };

  const handleUploadDoc = async (e) => {
    e.preventDefault();
    if (!docFormData.file) {
      toast.error('Seleziona un file');
      return;
    }

    try {
      const allTipi = [...tipiDocumentoPersonali, ...tipiDocumentoReddituali, ...tipiDocumentoDebitorie];
      const tipoLabel = allTipi.find(t => t.value === docFormData.tipo_documento)?.label || docFormData.tipo_documento;
      const ext = docFormData.file.name.includes('.') ? '.' + docFormData.file.name.split('.').pop().toLowerCase() : '';
      const userRef = selectedUser?.codice_fiscale || `${selectedUser?.nome || ''} ${selectedUser?.cognome || ''}`.trim();
      const renamedFilename = `${tipoLabel}${userRef ? '_' + userRef : ''}${ext}`.replace(/\s+/g, '_').replace(/\//g, '-');
      const formDataToSend = new FormData();
      formDataToSend.append('file', docFormData.file, renamedFilename);
      formDataToSend.append('user_id', selectedUser.id);
      formDataToSend.append('categoria', docFormData.categoria);
      formDataToSend.append('tipo_documento', docFormData.tipo_documento);
      if (docFormData.numero_documento) formDataToSend.append('numero_documento', docFormData.numero_documento);
      if (docFormData.rilasciato_da) formDataToSend.append('rilasciato_da', docFormData.rilasciato_da);
      if (docFormData.data_rilascio) formDataToSend.append('data_rilascio', docFormData.data_rilascio);
      if (docFormData.data_scadenza) formDataToSend.append('data_scadenza', docFormData.data_scadenza);
      if (docFormData.note) formDataToSend.append('note', docFormData.note);

      await documentiUtenteApi.upload(formDataToSend);
      toast.success('Documento caricato');
      await fetchDocumenti(selectedUser.id);
      setDocFormData({
        categoria: 'personali', tipo_documento: '', numero_documento: '',
        rilasciato_da: '', data_rilascio: '', data_scadenza: '', note: '', file: null
      });
      setDocFileInputKey(prev => prev + 1);
    } catch (error) {
      toast.error('Errore durante il caricamento');
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const response = await documentiUtenteApi.download(selectedUser.id, doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await documentiUtenteApi.delete(selectedUser.id, docId);
      toast.success('Documento eliminato');
      await fetchDocumenti(selectedUser.id);
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;
    try {
      await usersApi.delete(id);
      toast.success('Utente eliminato');
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'eliminazione';
      toast.error(message);
    }
  };

  const getTipiDocumentoOptions = () => {
    switch (docFormData.categoria) {
      case 'personali': return tipiDocumentoPersonali;
      case 'reddituali': return tipiDocumentoReddituali;
      case 'posizioni_debitorie': return tipiDocumentoDebitorie;
      default: return [];
    }
  };

  // Soglia inattività: 6 mesi (allineata al cron di disattivazione automatica)
  const inactiveCutoffIso = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString();
  })();
  const isUserInactive = (u) => {
    const ref = u.last_login_at || u.created_at;
    return ref ? ref < inactiveCutoffIso : true;
  };
  const isDeactivated = (u) => u.locked && u.locked_reason === 'disattivato_manualmente';

  const filteredUsers = users.filter(u => {
    const matchSearch = 
      u.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.cognome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.ragione_sociale?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchPwd = filterPwdPending ? u.password_setup_pending : true;
    const matchInactive = filterInactive ? isUserInactive(u) : true;
    // Di default nascondi i disattivati manualmente; col filtro attivo mostra SOLO loro
    const matchDeactivated = filterDeactivated ? isDeactivated(u) : !isDeactivated(u);
    return matchSearch && matchPwd && matchInactive && matchDeactivated && u.ruolo !== 'admin';
  });

  const pwdPendingCount = users.filter(u => u.password_setup_pending && u.ruolo !== 'admin').length;
  const inactiveCount = users.filter(u => u.ruolo !== 'admin' && isUserInactive(u)).length;
  const deactivatedCount = users.filter(u => u.ruolo !== 'admin' && isDeactivated(u)).length;

  // Ordinamento colonne
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" /> 
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Helper per nome visualizzazione (Cognome Nome o Ragione Sociale)
  const getDisplayName = (user) => {
    if (user.is_societa && user.ragione_sociale) {
      return user.ragione_sociale;
    }
    return `${user.cognome || ''} ${user.nome || ''}`.trim() || user.email;
  };

  // Ordinamento effettivo
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      let valA, valB;
      
      switch (sortColumn) {
        case 'cognome':
          valA = a.is_societa ? (a.ragione_sociale || '') : (a.cognome || '');
          valB = b.is_societa ? (b.ragione_sociale || '') : (b.cognome || '');
          break;
        case 'nome':
          valA = a.is_societa ? '' : (a.nome || '');
          valB = b.is_societa ? '' : (b.nome || '');
          break;
        case 'email':
          valA = a.email || '';
          valB = b.email || '';
          break;
        case 'codice_fiscale':
          valA = a.codice_fiscale || a.partita_iva || '';
          valB = b.codice_fiscale || b.partita_iva || '';
          break;
        case 'citta':
          valA = a.citta_residenza || '';
          valB = b.citta_residenza || '';
          break;
        default:
          valA = a[sortColumn] || '';
          valB = b[sortColumn] || '';
      }
      
      const comparison = String(valA).localeCompare(String(valB));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredUsers, sortColumn, sortDirection]);

  const adminUsers = users.filter(u => u.ruolo === 'admin');

  const getTipoBadgeColor = (tipo) => {
    switch (tipo) {
      case 'proprietario': return 'bg-blue-100 text-blue-700';
      case 'inquilino': return 'bg-green-100 text-green-700';
      case 'garante': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="utenti-page">
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Utenti</h1>
            <p className="text-slate-500 mt-1">Gestisci proprietari, inquilini e garanti</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => handleExport('xlsx')}
              data-testid="export-xlsx-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleExport('pdf')}
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button 
              onClick={() => { setFormData(getEmptyFormData()); setEditingItem(null); setCfValidation({ valid: null, message: '' }); setIbanValidation({ valid: null, message: '' }); setShowDialog(true); }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="new-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Utente
            </Button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <Tabs defaultValue="utenti" className="space-y-6">
          <TabsList>
            <TabsTrigger value="utenti" data-testid="tab-utenti">
              <UserCheck className="w-4 h-4 mr-2" />
              Utenti ({filteredUsers.length})
            </TabsTrigger>
            <TabsTrigger value="admin" data-testid="tab-admin">
              <Shield className="w-4 h-4 mr-2" />
              Amministratori ({adminUsers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="utenti">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Cerca per cognome, nome, email o ragione sociale..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-users"
                    />
                  </div>
                  <Button
                    variant={filterPwdPending ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterPwdPending(!filterPwdPending)}
                    className={filterPwdPending ? "bg-amber-500 hover:bg-amber-600" : ""}
                    data-testid="filter-pwd-pending"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Pwd da impostare ({pwdPendingCount})
                  </Button>
                  <Button
                    variant={filterInactive ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterInactive(!filterInactive)}
                    className={filterInactive ? "bg-red-500 hover:bg-red-600" : ""}
                    data-testid="filter-inactive"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Inattivi ({inactiveCount})
                  </Button>
                  <Button
                    variant={filterDeactivated ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilterDeactivated(!filterDeactivated)}
                    className={filterDeactivated ? "bg-slate-700 hover:bg-slate-800" : ""}
                    title={filterDeactivated ? "Mostra utenti attivi" : "Mostra solo gli utenti disattivati"}
                    data-testid="filter-deactivated"
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Disattivati ({deactivatedCount})
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
              <CardContent className="p-0 max-h-[70vh] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                    <TableRow>
                      <TableHead className="w-10 text-center">#</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 select-none"
                        onClick={() => handleSort('cognome')}
                      >
                        <div className="flex items-center">Nominativo {getSortIcon('cognome')}</div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 select-none"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center">Email {getSortIcon('email')}</div>
                      </TableHead>
                      <TableHead>PEC</TableHead>
                      <TableHead>SDI</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 select-none"
                        onClick={() => handleSort('codice_fiscale')}
                      >
                        <div className="flex items-center">CF/P.IVA {getSortIcon('codice_fiscale')}</div>
                      </TableHead>
                      <TableHead>Tel.</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50 select-none"
                        onClick={() => handleSort('citta')}
                      >
                        <div className="flex items-center">Città {getSortIcon('citta')}</div>
                      </TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                          Nessun utente trovato
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedUsers.map((item, index) => (
                        <TableRow key={item.id} data-testid={`user-row-${item.id}`}>
                          <TableCell className="text-center text-slate-400 font-mono text-xs">
                            {index + 1}
                          </TableCell>
                          <TableCell className="max-w-[220px]">
                            <div className="flex items-center gap-1.5">
                              {item.is_societa && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 text-blue-600 border-blue-300 shrink-0">S</Badge>
                              )}
                              <span className="font-medium truncate" title={item.is_societa ? item.ragione_sociale : `${item.cognome} ${item.nome}`}>
                                {item.is_societa 
                                  ? item.ragione_sociale 
                                  : `${item.cognome || ''} ${item.nome || ''}`.trim()}
                              </span>
                            </div>
                            {item.is_societa && item.nome && (
                              <div className="text-[11px] text-slate-400 mt-0.5 truncate" title={`Leg.Rap.: ${item.cognome} ${item.nome}`}>
                                Leg.Rap.: {item.cognome} {item.nome}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-slate-600">{item.email}</div>
                            {item.password_setup_pending && (
                              <div className="mt-0.5">
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-600 border-amber-300">
                                  <AlertCircle className="w-2.5 h-2.5 mr-1" />
                                  Pwd da impostare
                                </Badge>
                              </div>
                            )}
                            <div className="mt-0.5">
                              {item.locked && item.locked_reason === 'disattivato_manualmente' ? (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-red-600 border-red-300" data-testid={`badge-deactivated-${item.id}`}>
                                  <Ban className="w-2.5 h-2.5 mr-1" />
                                  Disattivato
                                </Badge>
                              ) : item.locked_reason === 'inattivita_6_mesi' ? (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-red-600 border-red-300" data-testid={`badge-locked-inactive-${item.id}`}>
                                  <Lock className="w-2.5 h-2.5 mr-1" />
                                  Bloccato per inattività
                                </Badge>
                              ) : !item.last_login_at ? (
                                <span className="text-[10px] text-slate-400">Mai effettuato l'accesso</span>
                              ) : isUserInactive(item) ? (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-red-600 border-red-300" data-testid={`badge-inactive-${item.id}`}>
                                  <Clock className="w-2.5 h-2.5 mr-1" />
                                  Inattivo dal {new Date(item.last_login_at).toLocaleDateString('it-IT')}
                                </Badge>
                              ) : (
                                <span className="text-[10px] text-slate-400">Ultimo accesso: {new Date(item.last_login_at).toLocaleDateString('it-IT')}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600 break-all" data-testid={`user-pec-${item.id}`}>
                              {item.pec || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-slate-500 font-mono" data-testid={`user-sdi-${item.id}`}>
                              {item.sdi || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs text-slate-500 font-mono">
                              {item.codice_fiscale || item.partita_iva || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-600">{item.telefono || '-'}</span>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const citta = item.is_societa ? (item.sede_legale_citta || item.citta_residenza) : item.citta_residenza;
                              const prov = item.is_societa ? (item.sede_legale_provincia || item.provincia_residenza) : item.provincia_residenza;
                              return citta ? (
                                <span className="text-sm text-slate-600">
                                  {citta}{prov ? ` (${prov})` : ''}
                                </span>
                              ) : '-';
                            })()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResetPassword(item)}
                                title="Reset/Invia link password"
                                data-testid={`reset-pwd-${item.id}`}
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDocuments(item)}
                                data-testid={`docs-user-${item.id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/admin/ghost/${item.id}`)}
                                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                title="Visualizza come questo utente"
                                data-testid={`ghost-user-${item.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setQuickEmailUser(item)}
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                title="Invia email rapida"
                                disabled={!item.email}
                                data-testid={`email-user-${item.id}`}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendWelcomeEmail(item)}
                                className={`${item.welcome_email_sent_at ? 'text-emerald-700 border-emerald-300' : 'text-indigo-600 border-indigo-300'} hover:bg-indigo-50`}
                                title={item.welcome_email_sent_at
                                  ? `Email benvenuto già inviata il ${new Date(item.welcome_email_sent_at).toLocaleDateString('it-IT')} — clicca per rinviare`
                                  : "Invia email di benvenuto"
                                }
                                disabled={!item.email}
                                data-testid={`welcome-email-${item.id}`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              {item.locked && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUnlock(item)}
                                  className="text-red-600 border-red-400 bg-red-50 hover:bg-red-100"
                                  title="Account bloccato - clicca per sbloccare"
                                  data-testid={`unlock-user-${item.id}`}
                                >
                                  <Lock className="w-4 h-4" />
                                </Button>
                              )}
                              {item.totp_enabled && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReset2FA(item)}
                                  className="text-green-700 border-green-400 bg-green-50 hover:bg-red-50 hover:text-red-600 hover:border-red-400"
                                  title="2FA attivo - clicca per resettare (es. perdita telefono)"
                                  data-testid={`reset-2fa-${item.id}`}
                                >
                                  <ShieldCheck className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setGdprDialog(item)}
                                className="text-purple-700 border-purple-300 hover:bg-purple-50"
                                title="Gestisci richiesta GDPR (Art. 15 — accesso ai dati)"
                                data-testid={`gdpr-user-${item.id}`}
                              >
                                <Shield className="w-4 h-4" />
                              </Button>
                              {item.ruolo !== 'admin' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleToggleActive(item)}
                                  className={item.locked
                                    ? 'text-emerald-700 border-emerald-400 bg-emerald-50 hover:bg-emerald-100'
                                    : 'text-red-600 border-red-300 hover:bg-red-50'}
                                  title={item.locked ? 'Riattiva utente (consenti accesso)' : 'Disattiva utente (blocca accesso)'}
                                  data-testid={`toggle-active-${item.id}`}
                                >
                                  {item.locked ? <UserCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(item)}
                                data-testid={`edit-user-${item.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(item.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-user-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="admin">
            <div className="flex justify-end mb-4">
              <Button onClick={handleNewAdmin} className="bg-slate-900 hover:bg-slate-800" data-testid="new-admin-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Amministratore
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 text-center">#</TableHead>
                      <TableHead>Amministratore</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Telefono</TableHead>
                      <TableHead>Data Creazione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adminUsers.map((item, index) => (
                      <TableRow key={item.id} data-testid={`admin-row-${item.id}`}>
                        <TableCell className="text-center text-slate-400 font-mono text-xs">
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Shield className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{item.cognome} {item.nome}</p>
                              <Badge className="bg-blue-100 text-blue-700">Admin</Badge>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{item.email}</TableCell>
                        <TableCell className="text-sm text-slate-600">{item.telefono || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleDateString('it-IT') : '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAdmin(item)}
                              data-testid={`edit-admin-${item.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteAdmin(item)}
                              data-testid={`delete-admin-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog Amministratore */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="max-w-md" data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle>{editingAdmin ? 'Modifica Amministratore' : 'Nuovo Amministratore'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdminSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="admin_nome">Nome *</Label>
                <Input
                  id="admin_nome"
                  value={adminFormData.nome}
                  onChange={(e) => setAdminFormData(prev => ({ ...prev, nome: e.target.value }))}
                  required
                  data-testid="admin-nome-input"
                />
              </div>
              <div>
                <Label htmlFor="admin_cognome">Cognome *</Label>
                <Input
                  id="admin_cognome"
                  value={adminFormData.cognome}
                  onChange={(e) => setAdminFormData(prev => ({ ...prev, cognome: e.target.value }))}
                  required
                  data-testid="admin-cognome-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="admin_email">Email *</Label>
              <Input
                id="admin_email"
                type="email"
                value={adminFormData.email}
                onChange={(e) => setAdminFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="admin_telefono">Telefono</Label>
              <Input
                id="admin_telefono"
                value={adminFormData.telefono}
                onChange={(e) => setAdminFormData(prev => ({ ...prev, telefono: e.target.value }))}
                data-testid="admin-telefono-input"
              />
            </div>
            <div>
              <Label htmlFor="admin_password">{editingAdmin ? 'Nuova Password (lascia vuoto per non cambiare)' : 'Password *'}</Label>
              <Input
                id="admin_password"
                type="password"
                value={adminFormData.password}
                onChange={(e) => setAdminFormData(prev => ({ ...prev, password: e.target.value }))}
                required={!editingAdmin}
                placeholder={editingAdmin ? 'Lascia vuoto per non cambiare' : ''}
                data-testid="admin-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdminDialog(false)}>Annulla</Button>
              <Button type="submit" data-testid="admin-submit-btn">
                {editingAdmin ? 'Salva Modifiche' : 'Crea Amministratore'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Utente */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Dati Anagrafici */}
              <Accordion type="single" collapsible defaultValue="anagrafica">
                <AccordionItem value="anagrafica">
                  <AccordionTrigger className="text-sm font-semibold">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> Dati Anagrafici
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-3 gap-4 pt-4">
                      {/* Toggle È una Società - in cima alla sezione */}
                      <div className="col-span-3">
                        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <input
                            type="checkbox"
                            id="is_societa"
                            checked={formData.is_societa || false}
                            onChange={(e) => handleFieldChange('is_societa', e.target.checked)}
                            className="rounded border-slate-300 h-4 w-4"
                            data-testid="is-societa-checkbox"
                          />
                          <Label htmlFor="is_societa" className="cursor-pointer font-medium">
                            <Briefcase className="w-4 h-4 inline mr-1" />
                            È una Società / Azienda
                          </Label>
                        </div>
                      </div>

                      {/* Dati Societari (visibili solo se is_societa) */}
                      {formData.is_societa && (
                        <>
                          <div className="col-span-3">
                            <Label>Ragione Sociale *</Label>
                            <Input 
                              value={formData.ragione_sociale || ''} 
                              onChange={(e) => handleFieldChange('ragione_sociale', e.target.value)} 
                              placeholder="Es: DIEFFE TERMOIDRAULICA SNC"
                              required={formData.is_societa}
                              data-testid="ragione-sociale-input"
                            />
                          </div>
                          <div>
                            <Label>Partita IVA *</Label>
                            <Input 
                              value={formData.partita_iva || ''} 
                              onChange={(e) => handleFieldChange('partita_iva', e.target.value)} 
                              placeholder="12345678901"
                              maxLength={11}
                              required={formData.is_societa}
                              data-testid="partita-iva-input"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>Codice Fiscale Società</Label>
                            <Input 
                              value={formData.codice_fiscale} 
                              onChange={(e) => handleFieldChange('codice_fiscale', e.target.value.toUpperCase())} 
                              placeholder="12345678901 o CODICE FISCALE"
                              maxLength={16}
                            />
                          </div>
                        </>
                      )}

                      {/* Persona Fisica */}
                      {!formData.is_societa && (
                        <>
                          <div>
                            <Label>Nome *</Label>
                            <Input value={formData.nome} onChange={(e) => handleFieldChange('nome', e.target.value)} required />
                          </div>
                          <div>
                            <Label>Cognome *</Label>
                            <Input value={formData.cognome} onChange={(e) => handleFieldChange('cognome', e.target.value)} required />
                          </div>
                          <div>
                            <Label>Data Nascita</Label>
                            <Input type="date" value={formData.data_nascita} onChange={(e) => handleFieldChange('data_nascita', e.target.value)} />
                          </div>
                          <div className="col-span-2 relative">
                            <Label>Luogo Nascita</Label>
                            <Input 
                              value={formData.luogo_nascita}
                              onChange={(e) => {
                                handleFieldChange('luogo_nascita', e.target.value);
                                debounced('nascita', () => searchComuniNascita(e.target.value));
                              }}
                              placeholder="Comune italiano o città estera..."
                            />
                            {comuniNascitaSuggestions.length > 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                                {comuniNascitaSuggestions.map((comune, idx) => (
                                  <div key={`${comune.nome}-${idx}`} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                                    onClick={() => selectComuneNascita(comune)}>
                                    <span className="font-medium">{comune.nome}</span>
                                    <span className="text-slate-500 ml-2">({comune.sigla_provincia})</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {searchingComuniNascita && <p className="text-xs text-slate-500 mt-1">Ricerca in corso...</p>}
                          </div>
                          <div>
                            <Label>Provincia Nascita</Label>
                            <Input value={formData.provincia_nascita} onChange={(e) => handleFieldChange('provincia_nascita', e.target.value)} placeholder="FI o EE" maxLength={2} />
                          </div>
                          {/* Codice Fiscale con validazione */}
                          <div className="col-span-3">
                            <Label>Codice Fiscale *</Label>
                            <div className="relative">
                              <Input 
                                value={formData.codice_fiscale} 
                                onChange={(e) => {
                                  const val = e.target.value.toUpperCase();
                                  handleFieldChange('codice_fiscale', val);
                                  if (val.length === 16) validateCodiceFiscale(val);
                                }} 
                                required 
                                maxLength={16}
                                placeholder="RSSMRA80A01H501X"
                                className={cfValidation.valid === false ? 'border-red-500' : cfValidation.valid === true ? 'border-green-500' : ''}
                              />
                              {cfValidation.valid === true && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}
                              {cfValidation.valid === false && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />}
                            </div>
                            {cfValidation.message && (
                              <p className={`text-xs mt-1 ${cfValidation.valid ? 'text-green-600' : 'text-red-600'}`}>{cfValidation.message}</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* SEDE LEGALE - Solo per società */}
                {formData.is_societa && (
                  <AccordionItem value="sede_legale">
                    <AccordionTrigger className="text-sm font-semibold">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> Sede Legale Società
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-3 gap-4 pt-4">
                        <div className="col-span-3">
                          <Label>Indirizzo Sede Legale</Label>
                          <Input 
                            value={formData.sede_legale_indirizzo || ''} 
                            onChange={(e) => handleFieldChange('sede_legale_indirizzo', e.target.value)} 
                            placeholder="Via Roma 1"
                            data-testid="sede-legale-indirizzo"
                          />
                        </div>
                        <div className="col-span-2 relative">
                          <Label>Cerca Comune Sede</Label>
                          <Input 
                            placeholder="Cerca comune..."
                            onChange={(e) => debounced('sede', () => searchComuniSede(e.target.value))}
                          />
                          {comuniSedeSuggestions.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                              {comuniSedeSuggestions.map((comune, idx) => (
                                <div key={`${comune.nome}-${comune.sigla_provincia || ''}-${idx}`} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                                  onClick={() => selectComuneSede(comune)}>
                                  <span className="font-medium">{comune.nome}</span>
                                  <span className="text-slate-500 ml-2">({comune.sigla_provincia})</span>
                                  {comune.cap && <span className="text-slate-400 ml-2">CAP: {comune.cap_list?.length > 1 ? `${comune.cap_list[0]}-${comune.cap_list[comune.cap_list.length-1]}` : comune.cap}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {searchingSede && <p className="text-xs text-slate-500 mt-1">Ricerca in corso...</p>}
                        </div>
                        <div />
                        <div>
                          <Label>CAP</Label>
                          <Input value={formData.sede_legale_cap || ''} onChange={(e) => handleFieldChange('sede_legale_cap', e.target.value)} placeholder="50100" maxLength={5} data-testid="sede-legale-cap" />
                        </div>
                        <div>
                          <Label>Città</Label>
                          <Input value={formData.sede_legale_citta || ''} onChange={(e) => handleFieldChange('sede_legale_citta', e.target.value)} placeholder="Firenze" data-testid="sede-legale-citta" />
                        </div>
                        <div>
                          <Label>Provincia</Label>
                          <Input value={formData.sede_legale_provincia || ''} onChange={(e) => handleFieldChange('sede_legale_provincia', e.target.value)} placeholder="FI" maxLength={2} data-testid="sede-legale-provincia" />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* LEGALE RAPPRESENTANTE - Solo per società */}
                {formData.is_societa && (
                  <AccordionItem value="rappresentante">
                    <AccordionTrigger className="text-sm font-semibold">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" /> Legale Rappresentante
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="grid grid-cols-3 gap-4 pt-4">
                        <div>
                          <Label>Nome *</Label>
                          <Input value={formData.nome} onChange={(e) => handleFieldChange('nome', e.target.value)} required placeholder="Mario" data-testid="nome-legale-input" />
                        </div>
                        <div>
                          <Label>Cognome *</Label>
                          <Input value={formData.cognome} onChange={(e) => handleFieldChange('cognome', e.target.value)} required placeholder="Rossi" data-testid="cognome-legale-input" />
                        </div>
                        <div>
                          <Label>Codice Fiscale Rappresentante</Label>
                          <Input value={formData.cf_rappresentante || ''} onChange={(e) => handleFieldChange('cf_rappresentante', e.target.value.toUpperCase())} maxLength={16} placeholder="RSSMRA80A01H501X" data-testid="cf-rappresentante" />
                        </div>
                        <div>
                          <Label>Data Nascita</Label>
                          <Input type="date" value={formData.rappresentante_data_nascita || ''} onChange={(e) => handleFieldChange('rappresentante_data_nascita', e.target.value)} data-testid="rappresentante-data-nascita" />
                        </div>
                        <div className="relative">
                          <Label>Luogo Nascita</Label>
                          <Input 
                            value={formData.rappresentante_luogo_nascita || ''}
                            onChange={(e) => {
                              handleFieldChange('rappresentante_luogo_nascita', e.target.value);
                              debounced('nascitaRapp', () => searchNascitaRapp(e.target.value));
                            }}
                            placeholder="Comune italiano o città estera..."
                          />
                          {comuniNascitaRappSuggestions.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                              {comuniNascitaRappSuggestions.map((comune, idx) => (
                                <div key={`${comune.nome}-${comune.sigla_provincia || ''}-${idx}`} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                                  onClick={() => selectNascitaRapp(comune)}>
                                  <span className="font-medium">{comune.nome}</span>
                                  <span className="text-slate-500 ml-2">({comune.sigla_provincia})</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {searchingNascitaRapp && <p className="text-xs text-slate-500 mt-1">Ricerca...</p>}
                        </div>
                        <div>
                          <Label>Prov. Nascita</Label>
                          <Input value={formData.rappresentante_provincia_nascita || ''} onChange={(e) => handleFieldChange('rappresentante_provincia_nascita', e.target.value)} placeholder="FI o EE" maxLength={2} />
                        </div>

                        {/* Residenza Rappresentante */}
                        <div className="col-span-3 mt-2 pt-3 border-t">
                          <p className="text-xs font-semibold text-slate-500 mb-3">RESIDENZA RAPPRESENTANTE</p>
                        </div>
                        <div className="col-span-3">
                          <Label>Indirizzo</Label>
                          <Input value={formData.rappresentante_indirizzo || ''} onChange={(e) => handleFieldChange('rappresentante_indirizzo', e.target.value)} placeholder="Via Bartolomeo della Gatta 1" />
                        </div>
                        <div className="col-span-2 relative">
                          <Label>Cerca Comune</Label>
                          <Input 
                            placeholder="Cerca comune..."
                            onChange={(e) => debounced('rappresentante', () => searchComuniRappresentante(e.target.value))}
                          />
                          {comuniRappresentanteSuggestions.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                              {comuniRappresentanteSuggestions.map((comune, idx) => (
                                <div key={`${comune.nome}-${comune.sigla_provincia || ''}-${idx}`} className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                                  onClick={() => selectComuneRappresentante(comune)}>
                                  <span className="font-medium">{comune.nome}</span>
                                  <span className="text-slate-500 ml-2">({comune.sigla_provincia})</span>
                                  {comune.cap && <span className="text-slate-400 ml-2">CAP: {comune.cap_list?.length > 1 ? `${comune.cap_list[0]}-${comune.cap_list[comune.cap_list.length-1]}` : comune.cap}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {searchingRappresentante && <p className="text-xs text-slate-500 mt-1">Ricerca...</p>}
                        </div>
                        <div />
                        <div>
                          <Label>CAP</Label>
                          <Input value={formData.rappresentante_cap || ''} onChange={(e) => handleFieldChange('rappresentante_cap', e.target.value)} placeholder="50100" maxLength={5} />
                        </div>
                        <div>
                          <Label>Città</Label>
                          <Input value={formData.rappresentante_citta || ''} onChange={(e) => handleFieldChange('rappresentante_citta', e.target.value)} placeholder="Firenze" />
                        </div>
                        <div>
                          <Label>Provincia</Label>
                          <Input value={formData.rappresentante_provincia || ''} onChange={(e) => handleFieldChange('rappresentante_provincia', e.target.value)} placeholder="FI" maxLength={2} />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                <AccordionItem value="contatti">
                  <AccordionTrigger className="text-sm font-semibold">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Contatti
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div>
                        <Label>Email *</Label>
                        <Input type="email" value={formData.email} onChange={(e) => handleFieldChange('email', e.target.value)} required />
                      </div>
                      {!editingItem && (
                        <div>
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                            <Key className="w-4 h-4 inline mr-2" />
                            L'utente riceverà un link per impostare la password.
                          </div>
                        </div>
                      )}
                      <div>
                        <Label>Telefono</Label>
                        <Input value={formData.telefono} onChange={(e) => handleFieldChange('telefono', e.target.value)} placeholder="+39 347..." />
                      </div>
                      <div>
                        <Label>PEC</Label>
                        <Input type="email" value={formData.pec} onChange={(e) => handleFieldChange('pec', e.target.value)} placeholder="nome@pec.it" />
                      </div>
                      <div className="col-span-2">
                        <Label>Altro Contatto</Label>
                        <Input value={formData.altro_contatto} onChange={(e) => handleFieldChange('altro_contatto', e.target.value)} placeholder="Altro recapito o note" />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* RESIDENZA - Solo per persona fisica (per società la residenza è nella sezione Rappresentante) */}
                {!formData.is_societa && (
                <AccordionItem value="residenza">
                  <AccordionTrigger className="text-sm font-semibold">
                    <div className="flex items-center gap-2">
                      <Home className="w-4 h-4" /> Residenza
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div className="col-span-2">
                        <Label>Indirizzo</Label>
                        <Input value={formData.indirizzo_residenza} onChange={(e) => handleFieldChange('indirizzo_residenza', e.target.value)} placeholder="Via Bartolomeo della Gatta n. 1" />
                      </div>
                      <div className="col-span-2 relative">
                        <Label>Cerca Comune (inizia a digitare)</Label>
                        <Input 
                          placeholder="Cerca comune italiano..."
                          onChange={(e) => {
                            debounced('residenza', () => searchComuni(e.target.value));
                          }}
                        />
                        {comuniSuggestions.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                            {comuniSuggestions.map((comune, idx) => (
                              <div 
                                key={`${comune.nome}-${comune.sigla_provincia || ''}-${idx}`} 
                                className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                                onClick={() => selectComune(comune)}
                              >
                                <span className="font-medium">{comune.nome}</span>
                                <span className="text-slate-500 ml-2">({comune.sigla_provincia})</span>
                                {comune.cap && (
                                  <span className="text-slate-400 ml-2">
                                    CAP: {comune.cap_list && comune.cap_list.length > 1 
                                      ? `${comune.cap_list[0]}-${comune.cap_list[comune.cap_list.length-1]}` 
                                      : comune.cap}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {searchingComuni && <p className="text-xs text-slate-500 mt-1">Ricerca in corso...</p>}
                      </div>
                      <div>
                        <Label>CAP</Label>
                        <Input value={formData.cap_residenza} onChange={(e) => handleFieldChange('cap_residenza', e.target.value)} placeholder="50143" maxLength={5} />
                      </div>
                      <div>
                        <Label>Città</Label>
                        <Input value={formData.citta_residenza} onChange={(e) => handleFieldChange('citta_residenza', e.target.value)} placeholder="Firenze" />
                      </div>
                      <div>
                        <Label>Provincia</Label>
                        <Input value={formData.provincia_residenza} onChange={(e) => handleFieldChange('provincia_residenza', e.target.value)} placeholder="FI" maxLength={2} />
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                )}

                <AccordionItem value="fiscale">
                  <AccordionTrigger className="text-sm font-semibold">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Dati Fiscali e Bancari
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <div>
                        <Label>SDI (Fatturazione)</Label>
                        <Input value={formData.sdi} onChange={(e) => handleFieldChange('sdi', e.target.value)} placeholder="0000000" maxLength={7} data-testid="sdi-input" />
                      </div>
                      <div className="col-span-2">
                        <Label>IBAN</Label>
                        <div className="relative">
                          <Input 
                            value={formData.iban} 
                            onChange={(e) => {
                              const val = e.target.value.toUpperCase().replace(/\s/g, '');
                              handleFieldChange('iban', val);
                              validateIban(val);
                            }} 
                            placeholder="IT60X0542811101000000123456"
                            maxLength={27}
                            className={ibanValidation.valid === false ? 'border-red-500' : ibanValidation.valid === true ? 'border-green-500' : ''}
                            data-testid="iban-input"
                          />
                          {ibanValidation.valid === true && (
                            <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                          )}
                          {ibanValidation.valid === false && (
                            <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                          )}
                        </div>
                        {ibanValidation.message && (
                          <p className={`text-xs mt-1 ${ibanValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                            {ibanValidation.message}
                          </p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <Label>Intestazione conto (Verifica VOP)</Label>
                        <Input 
                          value={formData.intestazione_vop} 
                          onChange={(e) => handleFieldChange('intestazione_vop', e.target.value)} 
                          placeholder="Compilare solo se diverso dal nome anagrafico"
                          data-testid="intestazione-vop-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Se valorizzato, verrà usato nella distinta SEPA al posto del nome anagrafico
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

              </Accordion>
            </form>
          </ScrollArea>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSubmit} className="bg-slate-900 hover:bg-slate-800">{editingItem ? 'Aggiorna' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Documenti Utente */}
      <Dialog open={showDocDialog} onOpenChange={(open) => {
        setShowDocDialog(open);
        if (!open) {
          setDocFormData({
            categoria: 'personali', tipo_documento: '', numero_documento: '',
            rilasciato_da: '', data_rilascio: '', data_scadenza: '', note: '', file: null
          });
          setDocFileInputKey(prev => prev + 1);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              Documenti di {selectedUser?.nome} {selectedUser?.cognome}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh]">
            <Tabs defaultValue="personali" className="w-full">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="personali">Personali</TabsTrigger>
                <TabsTrigger value="reddituali">Reddituali</TabsTrigger>
                <TabsTrigger value="posizioni_debitorie">Posizioni Debitorie</TabsTrigger>
              </TabsList>

              {['personali', 'reddituali', 'posizioni_debitorie'].map(cat => (
                <TabsContent key={cat} value={cat} className="space-y-4">
                  {/* Lista documenti esistenti */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Documenti Caricati</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tipo</TableHead>
                            {cat === 'personali' && <TableHead>Numero</TableHead>}
                            {cat === 'personali' && <TableHead>Scadenza</TableHead>}
                            <TableHead>File</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {documenti.filter(d => d.categoria === cat).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={cat === 'personali' ? 5 : 3} className="text-center py-4 text-slate-500">
                                Nessun documento
                              </TableCell>
                            </TableRow>
                          ) : (
                            documenti.filter(d => d.categoria === cat).map(doc => (
                              <TableRow key={doc.id}>
                                <TableCell className="font-medium">
                                  {[...tipiDocumentoPersonali, ...tipiDocumentoReddituali, ...tipiDocumentoDebitorie]
                                    .find(t => t.value === doc.tipo_documento)?.label || doc.tipo_documento}
                                </TableCell>
                                {cat === 'personali' && <TableCell>{doc.numero_documento || '-'}</TableCell>}
                                {cat === 'personali' && (
                                  <TableCell>
                                    {doc.data_scadenza ? new Date(doc.data_scadenza).toLocaleDateString('it-IT') : '-'}
                                  </TableCell>
                                )}
                                <TableCell className="text-sm text-slate-500">{doc.filename}</TableCell>
                                <TableCell className="text-right">
                                  <Button variant="ghost" size="sm" onClick={() => handleDownloadDoc(doc)}>
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(doc.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* Form upload */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Upload className="w-4 h-4" /> Carica Nuovo Documento
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleUploadDoc} className="space-y-4">
                        <input type="hidden" value={cat} />
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Tipo Documento *</Label>
                            <Select 
                              value={docFormData.categoria === cat ? docFormData.tipo_documento : ''} 
                              onValueChange={(v) => {
                                const isIdDoc = ['carta_identita', 'patente', 'passaporto'].includes(v);
                                setDocFormData({
                                  ...docFormData,
                                  categoria: cat,
                                  tipo_documento: v,
                                  ...(isIdDoc ? {} : { numero_documento: '', rilasciato_da: '', data_rilascio: '', data_scadenza: '' })
                                });
                              }}
                            >
                              <SelectTrigger data-testid={`doc-tipo-select-${cat}`}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                              <SelectContent>
                                {(cat === 'personali' ? tipiDocumentoPersonali :
                                  cat === 'reddituali' ? tipiDocumentoReddituali :
                                  tipiDocumentoDebitorie).map(t => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {cat === 'personali' && ['carta_identita', 'patente', 'passaporto'].includes(docFormData.categoria === cat ? docFormData.tipo_documento : '') && (
                            <div data-testid={`doc-numero-wrapper-${cat}`}>
                              <Label>Numero Documento</Label>
                              <Input 
                                data-testid={`doc-numero-input-${cat}`}
                                value={docFormData.categoria === cat ? docFormData.numero_documento : ''} 
                                onChange={(e) => setDocFormData({...docFormData, categoria: cat, numero_documento: e.target.value})} 
                              />
                            </div>
                          )}
                          {cat === 'personali' && ['carta_identita', 'patente', 'passaporto'].includes(docFormData.categoria === cat ? docFormData.tipo_documento : '') && (
                            <>
                              <div data-testid={`doc-rilasciato-wrapper-${cat}`}>
                                <Label>Rilasciato da</Label>
                                <Input 
                                  data-testid={`doc-rilasciato-input-${cat}`}
                                  value={docFormData.categoria === cat ? docFormData.rilasciato_da : ''} 
                                  onChange={(e) => setDocFormData({...docFormData, categoria: cat, rilasciato_da: e.target.value})} 
                                />
                              </div>
                              <div data-testid={`doc-data-rilascio-wrapper-${cat}`}>
                                <Label>Data Rilascio</Label>
                                <Input 
                                  type="date" 
                                  data-testid={`doc-data-rilascio-input-${cat}`}
                                  value={docFormData.categoria === cat ? docFormData.data_rilascio : ''} 
                                  onChange={(e) => setDocFormData({...docFormData, categoria: cat, data_rilascio: e.target.value})} 
                                />
                              </div>
                              <div data-testid={`doc-data-scadenza-wrapper-${cat}`}>
                                <Label>Data Scadenza</Label>
                                <Input 
                                  type="date" 
                                  data-testid={`doc-data-scadenza-input-${cat}`}
                                  value={docFormData.categoria === cat ? docFormData.data_scadenza : ''} 
                                  onChange={(e) => setDocFormData({...docFormData, categoria: cat, data_scadenza: e.target.value})} 
                                />
                              </div>
                            </>
                          )}
                          <div>
                            <Label>File *</Label>
                            <Input 
                              key={`file-input-${cat}-${docFileInputKey}`}
                              data-testid={`doc-file-input-${cat}`}
                              type="file" 
                              onChange={(e) => setDocFormData({...docFormData, categoria: cat, file: e.target.files[0]})} 
                            />
                          </div>
                        </div>
                        <Button 
                          type="submit" 
                          className="bg-slate-900 hover:bg-slate-800"
                          data-testid={`doc-upload-btn-${cat}`}
                          disabled={docFormData.categoria !== cat || !docFormData.tipo_documento || !docFormData.file}
                        >
                          <Upload className="w-4 h-4 mr-2" /> Carica
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Dialog Link Setup Password */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Link Setup Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">
              Invia questo link a <strong>{selectedUser?.nome} {selectedUser?.cognome}</strong> ({selectedUser?.email}) 
              per permettergli di impostare la propria password.
            </p>
            <div className="p-4 bg-slate-50 rounded-lg border">
              <Label className="text-xs text-slate-500 mb-2 block">Link di Setup (valido 7 giorni)</Label>
              <div className="flex gap-2">
                <Input 
                  value={`${window.location.origin}/setup-password?token=${passwordSetupLink}`}
                  readOnly 
                  className="font-mono text-xs"
                />
                <Button onClick={copySetupLink} variant="outline" size="sm">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Questo link scade dopo 7 giorni. Se l'utente non riesce ad accedere, puoi generare un nuovo link.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Chiudi</Button>
            <Button onClick={copySetupLink} className="bg-slate-900 hover:bg-slate-800">
              <Copy className="w-4 h-4 mr-2" />
              Copia Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Email Dialog */}
      <QuickEmailDialog
        open={!!quickEmailUser}
        onOpenChange={(o) => { if (!o) setQuickEmailUser(null); }}
        to={quickEmailUser?.email || ''}
        toName={quickEmailUser?.is_societa
          ? (quickEmailUser?.ragione_sociale || '')
          : `${quickEmailUser?.cognome || ''} ${quickEmailUser?.nome || ''}`.trim()}
        defaultSubject={quickEmailUser?._gdpr_prefill?.subject || "Comunicazione da Semplificaffitto.it"}
        defaultBody={quickEmailUser?._gdpr_prefill?.body || `Buongiorno ${quickEmailUser?.is_societa ? (quickEmailUser?.ragione_sociale || '') : (quickEmailUser?.nome || '')},\n\n`}
      />

      {/* GDPR Dialog — azioni admin per richieste GDPR */}
      <Dialog open={!!gdprDialog} onOpenChange={(o) => { if (!o) resetGdprDialog(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="gdpr-admin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Shield className="w-5 h-5" /> Gestione richiesta GDPR
            </DialogTitle>
            <DialogDescription>
              Strumenti per rispondere a una richiesta di esercizio diritti GDPR per{' '}
              <strong>{gdprDialog?.cognome} {gdprDialog?.nome}</strong> ({gdprDialog?.email}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <button
              onClick={() => handleGdprPdf(gdprDialog)}
              disabled={gdprLoading}
              className={`w-full text-left p-4 border-2 rounded-lg transition-colors disabled:opacity-50 ${
                gdprPdfDone ? 'bg-emerald-50 border-emerald-400' : 'bg-white border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50'
              }`}
              data-testid="gdpr-download-pdf-btn"
            >
              <div className="flex items-start gap-3">
                {gdprPdfDone ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" /> : <FileSpreadsheet className="w-5 h-5 text-emerald-600 mt-0.5" />}
                <div>
                  <p className="font-semibold text-slate-900">
                    {gdprLoading ? 'Generazione PDF...' : gdprPdfDone ? '✓ PDF scaricato' : '1. Scarica Riepilogo Dati (PDF)'}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Genera un documento leggibile con anagrafica, contratti, pagamenti, immobili.
                    Da allegare alla risposta email.
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleGdprEmail(gdprDialog)}
              disabled={!gdprDialog?.email}
              className={`w-full text-left p-4 border-2 rounded-lg transition-colors disabled:opacity-50 ${
                gdprEmailDone ? 'bg-blue-50 border-blue-400' : 'bg-white border-blue-200 hover:border-blue-400 hover:bg-blue-50'
              }`}
              data-testid="gdpr-compose-email-btn"
            >
              <div className="flex items-start gap-3">
                {gdprEmailDone ? <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" /> : <Mail className="w-5 h-5 text-blue-600 mt-0.5" />}
                <div>
                  <p className="font-semibold text-slate-900">
                    {gdprEmailDone ? '✓ Email composta' : '2. Componi Email di Risposta'}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Apre l'editor email con un testo standard precompilato
                    (riferimenti normativi, anonimizzazione 10 anni, contatti).
                  </p>
                </div>
              </div>
            </button>

            {/* Checkbox toggle per registrare nel registro accountability */}
            <label className="flex items-center gap-2 p-3 bg-slate-50 rounded cursor-pointer hover:bg-slate-100">
              <input
                type="checkbox"
                checked={gdprMarkOpen}
                onChange={(e) => setGdprMarkOpen(e.target.checked)}
                className="w-4 h-4 accent-purple-600"
                data-testid="gdpr-mark-checkbox"
              />
              <span className="text-sm font-medium text-slate-700">
                Marca come richiesta GDPR gestita (registro accountability)
              </span>
            </label>

            {gdprMarkOpen && (
              <div className="border border-purple-200 bg-purple-50/40 rounded p-3 space-y-2" data-testid="gdpr-mark-form">
                <div>
                  <label className="text-xs font-medium text-slate-700">Tipo richiesta</label>
                  <select
                    value={gdprTipo}
                    onChange={(e) => setGdprTipo(e.target.value)}
                    className="mt-1 w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                    data-testid="gdpr-mark-tipo"
                  >
                    <option value="accesso">Accesso (Art. 15)</option>
                    <option value="rettifica">Rettifica (Art. 16)</option>
                    <option value="cancellazione">Cancellazione (Art. 17)</option>
                    <option value="limitazione">Limitazione (Art. 18)</option>
                    <option value="portabilita">Portabilità (Art. 20)</option>
                    <option value="opposizione">Opposizione (Art. 21)</option>
                    <option value="altro">Altro</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Canale di ricezione</label>
                  <select
                    value={gdprCanale}
                    onChange={(e) => setGdprCanale(e.target.value)}
                    className="mt-1 w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                    data-testid="gdpr-mark-canale"
                  >
                    <option value="email">Email</option>
                    <option value="posta">Posta cartacea</option>
                    <option value="telefono">Telefono</option>
                    <option value="portale">Portale</option>
                    <option value="altro">Altro</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Esito</label>
                  <select
                    value={gdprEsito}
                    onChange={(e) => setGdprEsito(e.target.value)}
                    className="mt-1 w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                    data-testid="gdpr-mark-esito"
                  >
                    <option value="evasa">Evasa</option>
                    <option value="in_lavorazione">In lavorazione</option>
                    <option value="rifiutata">Rifiutata (motivare nelle note)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Note (opzionali)</label>
                  <textarea
                    value={gdprNote}
                    onChange={(e) => setGdprNote(e.target.value)}
                    placeholder="Es: data richiesta originale, eventuali specifiche..."
                    rows={2}
                    maxLength={2000}
                    className="mt-1 w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                    data-testid="gdpr-mark-note"
                  />
                </div>
                <Button
                  onClick={handleGdprMarkAsHandled}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  size="sm"
                  data-testid="gdpr-mark-save-btn"
                >
                  Salva nel registro accountability
                </Button>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
              <strong>Flusso consigliato:</strong> 1) scarica il PDF → 2) componi email → 3) allega il PDF
              all'email → 4) marca come gestita (registro accountability). L'azione di generazione PDF viene registrata negli audit log.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetGdprDialog}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UtentiPage;
